import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Link2, Plus } from "lucide-react";
import { useState } from "react";

import { ownerApprovalsApi, type RequestItem } from "../../api/collab";
import { shareLinksApi, shareUrl, type CreatedShareLink, type ShareLink } from "../../api/shareLinks";
import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Textarea,
  formatDateTime,
  formatRelative,
  useConfirm,
  useToast,
} from "../../ds";

const EXPIRY_OPTIONS: { value: string; label: string; days: number | null }[] = [
  { value: "7", label: "7 days", days: 7 },
  { value: "30", label: "30 days", days: 30 },
  { value: "90", label: "90 days", days: 90 },
  { value: "never", label: "Never (until turned off)", days: null },
];

const STATE: Record<ShareLink["state"], { label: string; tone: "success" | "neutral" | "warning" }> = {
  active: { label: "Active", tone: "success" },
  expired: { label: "Expired", tone: "warning" },
  revoked: { label: "Turned off", tone: "neutral" },
};

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Links that open the published report without signing in. */
export function ShareLinksTab({ reportId, base, published }: { reportId: string; base: "/admin" | "/owner"; published: boolean }) {
  const api = shareLinksApi(base);
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const key = ["share-links", base, reportId];
  const links = useQuery({ queryKey: key, queryFn: () => api.list(reportId) });
  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState("30");
  const [created, setCreated] = useState<CreatedShareLink | null>(null);
  // v3.0: owners can't create public links themselves -- they request one and IT decides.
  const isOwner = base === "/owner";
  const reqKey = ["link-requests", reportId];
  const requests = useQuery({ queryKey: reqKey, queryFn: () => ownerApprovalsApi.listLinkRequests(reportId), enabled: isOwner });
  const [requestOpen, setRequestOpen] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [reason, setReason] = useState("");
  const [reqExpiry, setReqExpiry] = useState("30");
  const [delivered, setDelivered] = useState<{ path: string; expires_at: string | null } | null>(null);
  const request = useMutation({
    mutationFn: () => ownerApprovalsApi.requestLink(reportId, { recipients, reason, expires_in_days: EXPIRY_OPTIONS.find((o) => o.value === reqExpiry)?.days ?? 30 }),
    onSuccess: () => {
      setRequestOpen(false);
      setRecipients("");
      setReason("");
      toast.success("Request sent", "IT will review it. You'll see the answer here and in your notifications.");
      qc.invalidateQueries({ queryKey: reqKey });
    },
    onError: (e) => toast.error("Couldn't send the request", e instanceof Error ? e.message : undefined),
  });
  const cancelRequest = useMutation({
    mutationFn: (r: RequestItem) => ownerApprovalsApi.cancelLinkRequest(r.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: reqKey }),
    onError: (e) => toast.error("Couldn't cancel", e instanceof Error ? e.message : undefined),
  });
  const collect = useMutation({
    mutationFn: (r: RequestItem) => ownerApprovalsApi.collectLink(r.id),
    onSuccess: (d) => {
      setDelivered({ path: d.path, expires_at: d.expires_at });
      qc.invalidateQueries({ queryKey: reqKey });
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast.error("Couldn't get the link", e instanceof Error ? e.message : undefined),
  });

  const create = useMutation({
    mutationFn: () => api.create(reportId, { label, expires_in_days: EXPIRY_OPTIONS.find((o) => o.value === expiry)?.days ?? 30 }),
    onSuccess: (link) => {
      setCreateOpen(false);
      setLabel("");
      setExpiry("30");
      setCreated(link);
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast.error("Couldn't create the link", e instanceof Error ? e.message : undefined),
  });
  const revoke = useMutation({
    mutationFn: (link: ShareLink) => api.revoke(reportId, link.id),
    onSuccess: () => {
      toast.success("Link turned off", "Anyone using it now sees “link turned off”.");
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast.error("Couldn't turn the link off", e instanceof Error ? e.message : undefined),
  });

  const createdUrl = created ? shareUrl(created.path) : "";

  return (
    <Card
      title="Share links"
      subtitle={isOwner ? "A public link opens the published report without sign-in, so it needs IT approval. Ask for one below; you can turn any link off yourself." : "Anyone with a link can open the published report and use its filters — no sign-in. They see what you can see. Turn a link off at any time."}
      actions={isOwner
        ? <Button variant="primary" icon={Plus} disabled={!published} title={published ? undefined : "Publish the report first"} onClick={() => setRequestOpen(true)}>Request link</Button>
        : <Button variant="primary" icon={Plus} disabled={!published} title={published ? undefined : "Publish the report first"} onClick={() => setCreateOpen(true)}>New link</Button>}
    >
      {!published ? (
        <div style={{ marginBottom: 12 }}>
          <Banner tone="info" title="Publish first">A share link always shows the published version, so this report needs to be published before you can share it.</Banner>
        </div>
      ) : null}
      {isOwner && (requests.data ?? []).length > 0 ? (
        <div className="ds-stack" style={{ gap: 8, marginBottom: 16 }}>
          <div className="ds-small ds-muted">Link requests</div>
          {(requests.data ?? []).map((r) => (
            <div key={r.id} className="ds-row" style={{ gap: 10, padding: "8px 10px", border: "1px solid var(--divider)", borderRadius: 8, flexWrap: "wrap" }}>
              <Badge tone={r.status === "approved" ? "success" : r.status === "pending" ? "warning" : "neutral"} dot>
                {r.status === "pending" ? "Waiting for IT" : r.status === "approved" ? "Approved" : r.status === "rejected" ? "Not approved" : "Cancelled"}
              </Badge>
              <div className="ds-grow" style={{ minWidth: 200 }}>
                <div style={{ fontWeight: 600 }}>{r.recipients}</div>
                <div className="ds-small ds-muted">{r.reason}</div>
                {r.decision_note ? <div className="ds-small">IT: {r.decision_note}</div> : null}
              </div>
              <span className="ds-small ds-faint">{formatRelative(r.decided_at ?? r.created_at)}</span>
              {r.link_ready ? <Button size="sm" variant="primary" icon={Copy} loading={collect.isPending && collect.variables?.id === r.id} onClick={() => collect.mutate(r)}>Get the link</Button> : null}
              {r.status === "pending" ? <Button size="sm" variant="ghost" loading={cancelRequest.isPending && cancelRequest.variables?.id === r.id} onClick={() => cancelRequest.mutate(r)}>Cancel request</Button> : null}
            </div>
          ))}
        </div>
      ) : null}
      <DataTable<ShareLink>
        rows={links.data}
        loading={links.isLoading}
        rowKey={(l) => l.id}
        empty={<EmptyState icon={Link2} title="No share links yet" description="Create a link to send this report to people who don't have a platform account." />}
        columns={[
          { key: "label", header: "Name", cell: (l) => <span><strong>{l.label}</strong> <span className="ds-small ds-faint">…/s/{l.token_prefix}…</span></span> },
          { key: "state", header: "Status", cell: (l) => <Badge tone={STATE[l.state].tone} dot>{STATE[l.state].label}</Badge> },
          { key: "expires", header: "Expires", cell: (l) => <span className="ds-muted" title={l.expires_at ? formatDateTime(l.expires_at) : undefined}>{l.expires_at ? formatDateTime(l.expires_at) : "Never"}</span> },
          { key: "opened", header: "Opened", cell: (l) => <span className="ds-muted">{l.open_count ? `${l.open_count}× · last ${formatRelative(l.last_used_at ?? l.created_at)}` : "Not yet"}</span> },
          { key: "by", header: "Created", cell: (l) => <span className="ds-muted" title={formatDateTime(l.created_at)}>{l.created_by_name ?? "—"} · {formatRelative(l.created_at)}</span> },
          {
            key: "act",
            header: "",
            align: "right",
            cell: (l) => l.state === "active" ? (
              <Button
                size="sm"
                variant="ghost"
                loading={revoke.isPending && revoke.variables?.id === l.id}
                onClick={async () => {
                  if (await confirm({ title: `Turn off “${l.label}”?`, message: "Anyone with this link loses access immediately. You can create a new link later.", confirmLabel: "Turn off link", tone: "danger" })) revoke.mutate(l);
                }}
              >
                Turn off
              </Button>
            ) : null,
          },
        ]}
      />

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New share link"
        description="The link opens the published report, without sign-in, for anyone who has it."
        footer={<><Button onClick={() => setCreateOpen(false)}>Cancel</Button><Button variant="primary" icon={Link2} loading={create.isPending} onClick={() => create.mutate()}>Create link</Button></>}
      >
        <div className="ds-stack">
          <Field label="Name" hint="So you can tell links apart, e.g. who you're sending it to.">
            <Input value={label} maxLength={100} placeholder="e.g. Board members" onChange={(e) => setLabel(e.target.value)} autoFocus />
          </Field>
          <Field label="Link works for">
            <Select value={expiry} onChange={(e) => setExpiry(e.target.value)}>
              {EXPIRY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </Field>
        </div>
      </Modal>

      <Modal
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        title="Request a public link"
        description="An IT administrator reviews every public link. Say who it is for and why."
        footer={<><Button onClick={() => setRequestOpen(false)}>Cancel</Button><Button variant="primary" icon={Link2} loading={request.isPending} disabled={recipients.trim().length < 3 || reason.trim().length < 10} onClick={() => request.mutate()}>Send request</Button></>}
      >
        <div className="ds-stack">
          <Field label="Who is it for?" hint="Names or organisations, e.g. “Board of Partner Co”.">
            <Input value={recipients} maxLength={2000} onChange={(e) => setRecipients(e.target.value)} autoFocus />
          </Field>
          <Field label="Why do they need it?" hint="A sentence or two (at least 10 characters).">
            <Textarea value={reason} maxLength={2000} rows={3} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <Field label="Link works for">
            <Select value={reqExpiry} onChange={(e) => setReqExpiry(e.target.value)}>
              {EXPIRY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </Field>
        </div>
      </Modal>

      <Modal
        open={!!delivered}
        onClose={() => setDelivered(null)}
        title="Your approved link"
        description="Copy it now — for security it can't be shown again."
        footer={<Button variant="primary" onClick={() => setDelivered(null)}>Done</Button>}
      >
        {delivered ? (
          <div className="ds-stack">
            <div className="ds-row" style={{ gap: 8 }}>
              <Input readOnly value={shareUrl(delivered.path)} onFocus={(e) => e.currentTarget.select()} style={{ flex: 1, fontFamily: "var(--font-mono, monospace)" }} />
              <Button variant="primary" icon={Copy} onClick={async () => { if (await copyText(shareUrl(delivered.path))) toast.success("Link copied"); else toast.info("Copy it manually", "Select the link and press Ctrl+C."); }}>Copy</Button>
            </div>
            <div className="ds-small ds-muted">{delivered.expires_at ? `Works until ${formatDateTime(delivered.expires_at)}.` : "Works until you turn it off."}</div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={!!created}
        onClose={() => setCreated(null)}
        title="Your share link"
        description="Copy it now — for security it can't be shown again. (You can always create another.)"
        footer={<Button variant="primary" onClick={() => setCreated(null)}>Done</Button>}
      >
        <div className="ds-stack">
          <div className="ds-row" style={{ gap: 8 }}>
            <Input readOnly value={createdUrl} onFocus={(e) => e.currentTarget.select()} style={{ flex: 1, fontFamily: "var(--font-mono, monospace)" }} />
            <Button
              variant="primary"
              icon={Copy}
              onClick={async () => {
                if (await copyText(createdUrl)) toast.success("Link copied");
                else toast.info("Copy it manually", "Select the link and press Ctrl+C.");
              }}
            >
              Copy
            </Button>
          </div>
          {created ? <div className="ds-small ds-muted">{created.expires_at ? `Works until ${formatDateTime(created.expires_at)}.` : "Works until you turn it off."}</div> : null}
        </div>
      </Modal>
    </Card>
  );
}
